import React, { useEffect, useState } from "react";
import axios from "axios";
import { Fuel, Route as RouteIcon, Users, TrafficCone, Gauge, ChevronRight, MapPin, TriangleAlert } from "lucide-react";
import { apiBaseUrl, authHeaders, toUtcDate, isAdmin } from "./shared";
import LoadingState from "./LoadingState";
import IdleRouteModal from "../../components/IdleRouteModal";
import ActiveNowIdle from "../../components/ActiveNowIdle";

// DRIVER-FIRST Idle Movement (Fleet Admin). "Idle Movement" = actual GPS movement
// recorded while the truck had NO load destination -- captured driver-aware since
// the Sep-24-2026 reset (immutable breadcrumb driver_id, never current_driver_id).
// Mileage is canonical episode-aware; deadhead is kept separate; Unattributed
// movement is never dropped -- it still counts toward fleet totals.

function todayIso() { return new Date().toISOString().slice(0, 10); }
function daysAgoIso(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); }
const miles = (n) => `${Math.round(Number(n || 0)).toLocaleString("en-US")} mi`;
function fmtDur(s) { if (s == null) return "—"; const h = Math.floor(s / 3600), m = Math.round((s % 3600) / 60); return h ? `${h}h ${m}m` : `${m}m`; }
function fmtWhen(iso) { return iso ? toUtcDate(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"; }
function fmtDate(iso) { return iso ? toUtcDate(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—"; }
const UNATTR = "__unattributed__";

export default function IdleMiles() {
  const [start, setStart] = useState(daysAgoIso(14));
  const [end, setEnd] = useState(todayIso());
  const [scope, setScope] = useState(isAdmin() ? "all" : "mine");  // dispatchers default to their own trucks
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);          // expanded row key (driver_id | UNATTR)
  const [segs, setSegs] = useState({});            // key -> {loading, rows}
  const [route, setRoute] = useState(null);        // {segId, unitNumber, driverName, laneLabel}

  useEffect(() => {
    setLoading(true); setError(null); setOpen(null); setSegs({});
    axios
      .get(`${apiBaseUrl}/fleet/idle-movement/by-driver`, { ...authHeaders(), params: { start, end, scope } })
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));
  }, [start, end, scope]);

  const totals = data?.totals || {};
  const drivers = data?.drivers || [];
  const unattr = data?.unattributed || null;

  function toggle(key, driverId) {
    if (open === key) { setOpen(null); return; }
    setOpen(key);
    if (segs[key]) return;
    setSegs((s) => ({ ...s, [key]: { loading: true, rows: [] } }));
    const params = { start, end, scope };
    if (key !== UNATTR) params.driver_id = driverId;
    axios
      .get(`${apiBaseUrl}/fleet/idle-movement/segments`, { ...authHeaders(), params })
      .then((res) => {
        let rows = res.data.segments || [];
        if (key === UNATTR) rows = rows.filter((r) => (r.drivers || []).some((d) => d.driver_id == null));
        setSegs((s) => ({ ...s, [key]: { loading: false, rows } }));
      })
      .catch((err) => setSegs((s) => ({ ...s, [key]: { loading: false, rows: [], error: err.response?.data?.detail || err.message } })));
  }

  const lane = (sg) => {
    const a = sg.previous_load ? (sg.previous_load.destination || "Load A") : "No previous load";
    const b = sg.eventual_next_load ? (sg.eventual_next_load.origin || "Load B") : "No next load";
    return `${a} → ${b}`;
  };

  return (
    <div>
      {route && (
        <IdleRouteModal
          segId={route.segId} unitNumber={route.unitNumber} driverName={route.driverName} laneLabel={route.laneLabel}
          onClose={() => setRoute(null)}
        />
      )}

      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Idle Movement</h1>
          <div style={styles.subtitle}>Where each driver drove while no load destination was associated with the movement — actual GPS evidence.</div>
        </div>
        <div style={styles.dateRange}>
          <div style={styles.scopeToggle}>
            <button type="button" onClick={() => setScope("mine")} style={{ ...styles.scopeBtn, ...(scope === "mine" ? styles.scopeBtnActive : {}) }}>My trucks</button>
            <button type="button" onClick={() => setScope("all")} style={{ ...styles.scopeBtn, ...(scope === "all" ? styles.scopeBtnActive : {}) }}>All trucks</button>
          </div>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} style={styles.dateInput} max={end} />
          <span style={styles.dateSep}>to</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} style={styles.dateInput} min={start} max={todayIso()} />
        </div>
      </div>

      {data?.tracking_started_at && (
        <div style={styles.trackBanner}>
          <MapPin size={14} color="#2563eb" />
          Idle Movement tracking started {fmtDate(data.tracking_started_at)} — figures below cover only driver-aware data captured since then.
        </div>
      )}

      <ActiveNowIdle setRoute={setRoute} scope={scope} />

      <div style={styles.histHead}>Historical Idle Movement</div>
      {error && <div style={styles.errorBox}>Couldn't load idle movement: {error}</div>}
      {loading && <LoadingState label="Loading idle movement…" />}

      <div style={styles.statGrid}>
        <StatCard label="Idle Miles" value={loading ? "…" : miles(totals.idle_miles)} icon={<RouteIcon size={16} />} tone="amber" />
        <StatCard label="Attributed %" value={loading ? "…" : (totals.attributed_pct == null ? "—" : `${totals.attributed_pct}%`)} icon={<Gauge size={16} />} tone="green" />
        <StatCard label="Drivers w/ Idle" value={loading ? "…" : (totals.drivers_with_idle ?? 0)} icon={<Users size={16} />} tone="blue" />
        <StatCard label="Idle Segments" value={loading ? "…" : (totals.segment_count ?? 0)} icon={<TrafficCone size={16} />} tone="slate" />
        <StatCard label="Largest Segment" value={loading ? "…" : miles(totals.largest_segment_miles)} icon={<Fuel size={16} />} tone="amber" />
      </div>

      {!loading && !error && drivers.length === 0 && !unattr && (
        <div style={styles.card}><div style={styles.emptyNote}>No idle movement recorded in this range yet — every truck was loaded, deadheading, or stationary.</div></div>
      )}

      {(drivers.length > 0 || unattr) && (
        <div style={styles.card}>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, width: 26 }} />
                  <th style={styles.th}>Driver</th>
                  <th style={styles.thR}>Idle Miles</th>
                  <th style={styles.thR}>Segments</th>
                  <th style={styles.thR}>Largest</th>
                  <th style={styles.thR}>Trucks</th>
                  <th style={styles.thR}>Last Idle Movement</th>
                </tr>
              </thead>
              <tbody>
                {drivers.map((d) => (
                  <DriverRows key={d.driver_id} rowKey={d.driver_id} driverId={d.driver_id} name={d.driver_name}
                    d={d} open={open} toggle={toggle} seg={segs[d.driver_id]} lane={lane} setRoute={setRoute} />
                ))}
                {unattr && (
                  <DriverRows rowKey={UNATTR} driverId={null} name="Unattributed" unattributed d={unattr}
                    open={open} toggle={toggle} seg={segs[UNATTR]} lane={lane} setRoute={setRoute} />
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={styles.caption}>
        Idle miles are episode-aware GPS distance (consecutive breadcrumbs only; &gt;10&nbsp;min gaps and GPS teleports are never bridged). Deadhead-to-next-pickup is measured separately and shown in the drilldown. Driver ownership is the immutable driver captured at each breadcrumb; movement we can't attribute to a single driver is shown as Unattributed, never dropped.
      </div>
    </div>
  );
}

function DriverRows({ rowKey, driverId, name, d, unattributed, open, toggle, seg, lane, setRoute }) {
  const isOpen = open === rowKey;
  return (
    <>
      <tr onClick={() => toggle(rowKey, driverId)} style={{ ...styles.trClickable, ...(unattributed ? styles.unattrRow : {}) }}>
        <td style={styles.td}><ChevronRight size={14} style={{ transform: isOpen ? "rotate(90deg)" : "none", transition: "transform .15s", color: "var(--muted)" }} /></td>
        <td style={{ ...styles.td, fontWeight: 700, color: unattributed ? "#c07f1f" : "var(--ink)" }}>
          {unattributed ? <span style={styles.unattrBadge}><TriangleAlert size={12} /> Unattributed</span> : name}
        </td>
        <td style={{ ...styles.tdR, fontWeight: 800, color: "#c07f1f" }}>{miles(d.idle_miles)}</td>
        <td style={styles.tdR}>{d.segment_count ?? 0}</td>
        <td style={styles.tdR}>{unattributed ? "—" : miles(d.largest_segment_miles)}</td>
        <td style={styles.tdR}>{d.truck_count ?? 0}</td>
        <td style={styles.tdR}>{fmtWhen(d.last_idle_at)}</td>
      </tr>
      {isOpen && (
        <tr>
          <td colSpan={7} style={styles.drillCell}>
            {seg?.loading && <div style={styles.drillNote}>Loading segments…</div>}
            {seg?.error && <div style={styles.drillErr}>Couldn't load segments: {seg.error}</div>}
            {seg && !seg.loading && !seg.error && seg.rows.length === 0 && <div style={styles.drillNote}>No segments.</div>}
            {seg && !seg.loading && seg.rows.length > 0 && (
              <div style={styles.segList}>
                {seg.rows.map((sg) => {
                  const shown = unattributed
                    ? (sg.drivers.find((x) => x.driver_id == null)?.idle_miles ?? 0)
                    : (sg.driver_idle_miles ?? sg.idle_miles);
                  return (
                    <div key={sg.seg_id} style={styles.segCard}>
                      <div style={styles.segMain}>
                        <div style={styles.segLane}>{lane(sg)}</div>
                        <div style={styles.segMeta}>
                          <span style={styles.segTruck}>Truck #{sg.unit_number || "?"}</span>
                          <span style={styles.segIdle}>Idle {miles(shown)}</span>
                          <span>Deadhead after {miles(sg.deadhead_miles_after)}</span>
                          <span>{fmtDur(sg.duration_s)}</span>
                          <span>{sg.breadcrumb_count} GPS pts · {sg.episode_count} ep</span>
                          {sg.gps_gap_count > 0 && <span style={styles.segFlag}>{sg.gps_gap_count} gap{sg.gps_gap_count === 1 ? "" : "s"}</span>}
                          <span style={styles.segWhen}>{fmtWhen(sg.start_at)}</span>
                        </div>
                      </div>
                      <button style={styles.viewBtn}
                        onClick={() => setRoute({ segId: sg.seg_id, unitNumber: sg.unit_number, driverName: unattributed ? "Unattributed" : name, laneLabel: lane(sg) })}>
                        <MapPin size={13} /> View Route
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

const TONE = {
  amber: { bg: "rgba(242,163,61,0.12)", fg: "#c07f1f" },
  green: { bg: "rgba(76,174,109,0.12)", fg: "#2f9e5f" },
  blue: { bg: "rgba(37,99,235,0.10)", fg: "#2563eb" },
  slate: { bg: "rgba(100,116,139,0.12)", fg: "#64748b" },
};
function StatCard({ label, value, icon, tone = "amber" }) {
  const t = TONE[tone];
  return (
    <div style={styles.statCard}>
      <div style={{ ...styles.statIcon, background: t.bg, color: t.fg }}>{icon}</div>
      <div><div style={styles.statValue}>{value}</div><div style={styles.statLabel}>{label}</div></div>
    </div>
  );
}

const styles = {
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 12 },
  title: { fontSize: 22, fontWeight: 800, color: "var(--ink)", margin: 0 },
  subtitle: { fontSize: 13, color: "var(--muted)", marginTop: 4, maxWidth: 640 },
  dateRange: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  scopeToggle: { display: "inline-flex", border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden" },
  scopeBtn: { fontSize: 12, fontWeight: 600, color: "var(--ink-2)", background: "var(--card)", border: "none", padding: "7px 12px", cursor: "pointer" },
  scopeBtnActive: { background: "var(--brand, #2563eb)", color: "#fff" },
  dateInput: { fontSize: 12.5, color: "var(--ink)", background: "var(--card)", border: "1px solid var(--line)", borderRadius: 8, padding: "7px 10px" },
  dateSep: { fontSize: 12, color: "var(--muted)" },

  histHead: { fontSize: 15, fontWeight: 800, color: "var(--ink)", margin: "4px 0 14px" },
  trackBanner: { display: "flex", alignItems: "center", gap: 8, background: "rgba(37,99,235,0.06)", border: "1px solid rgba(37,99,235,0.2)",
    color: "var(--ink-2)", borderRadius: "var(--radius-md)", padding: "10px 14px", fontSize: 12.5, marginBottom: 16 },
  errorBox: { background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)", color: "#dc2626", borderRadius: "var(--radius-md)", padding: "12px 16px", fontSize: 13, marginBottom: 16 },

  statGrid: { display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, marginBottom: 20 },
  statCard: { display: "flex", alignItems: "center", gap: 12, background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", padding: "14px 16px", boxShadow: "var(--shadow-xs)" },
  statIcon: { width: 34, height: 34, borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  statValue: { fontSize: 19, fontWeight: 800, color: "var(--ink)", lineHeight: 1.2 },
  statLabel: { fontSize: 11.5, color: "var(--muted)", marginTop: 2 },

  card: { background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-lg)", padding: 8, boxShadow: "var(--shadow-xs)" },
  emptyNote: { fontSize: 12.5, color: "var(--muted)", padding: 12 },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12.5 },
  th: { textAlign: "left", padding: "10px 8px", color: "var(--muted)", fontWeight: 600, borderBottom: "1px solid var(--line)" },
  thR: { textAlign: "right", padding: "10px 8px", color: "var(--muted)", fontWeight: 600, borderBottom: "1px solid var(--line)" },
  trClickable: { cursor: "pointer", borderBottom: "1px solid var(--line)" },
  unattrRow: { background: "rgba(242,163,61,0.05)" },
  unattrBadge: { display: "inline-flex", alignItems: "center", gap: 5, color: "#c07f1f" },
  td: { padding: "10px 8px", color: "var(--ink-2)" },
  tdR: { padding: "10px 8px", color: "var(--ink-2)", textAlign: "right" },

  drillCell: { padding: 0, background: "var(--bg, rgba(0,0,0,0.02))", borderBottom: "1px solid var(--line)" },
  drillNote: { fontSize: 12, color: "var(--muted)", padding: "12px 16px" },
  drillErr: { fontSize: 12, color: "#dc2626", padding: "12px 16px" },
  segList: { display: "flex", flexDirection: "column", gap: 8, padding: 12 },
  segCard: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, background: "var(--card)", border: "1px solid var(--line)", borderRadius: 10, padding: "10px 14px" },
  segMain: { minWidth: 0 },
  segLane: { fontSize: 13, fontWeight: 700, color: "var(--ink)", marginBottom: 4 },
  segMeta: { display: "flex", flexWrap: "wrap", gap: 12, fontSize: 12, color: "var(--muted)" },
  segTruck: { fontWeight: 600, color: "var(--ink-2)" },
  segIdle: { fontWeight: 700, color: "#c07f1f" },
  segWhen: { color: "var(--muted)" },
  segFlag: { color: "#c07f1f" },
  viewBtn: { display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: "var(--brand, #2563eb)", background: "var(--card)", border: "1px solid var(--brand, #2563eb)", borderRadius: 8, padding: "7px 12px", cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" },
  caption: { fontSize: 11, color: "var(--muted)", marginTop: 12, lineHeight: 1.5, maxWidth: 900 },
};
